import React, { useMemo } from 'react';

import Modal from '../../components/Modal';
import ActionButtons from '../../components/ActionButtons';
import clsx from 'clsx';
import { errorStyles, inputStyles } from '../../styles/styles';
import { findGroupForTypeId, findOptionForTypeId } from '../../catalog/catalogGroups';

const UploadModal = ({ catalogGroups = [], isLoading, isOpen, form, handleSubmit, handleChange, handleFileChange, onClose, isEdit = false, errors }) => {
    const labels = useMemo(
        () => ({
            normalLabel: isEdit ? 'Update Upload' : 'Create Upload',
            loadingLabel: isEdit ? 'Updating...' : 'Creating...'
        }),
        [isEdit]
    );

    const selectedGroup = catalogGroups.find((group) => group.id === form?.catalog_group_id) || findGroupForTypeId(catalogGroups, form?.material_type_id);
    const selectedOption = findOptionForTypeId(selectedGroup, form?.material_type_id);
    const showSubcategory = Boolean(selectedGroup?.showSubFilter || (selectedGroup?.options?.length || 0) > 1);

    const selectedMaterialType =
        selectedOption?.types.find((type) => type.id === Number(form?.material_type_id)) ||
        selectedGroup?.types.find((type) => type.id === Number(form?.material_type_id));
    const isAudioOrVideo =
        selectedMaterialType?.name?.toLowerCase().includes('audio') || selectedMaterialType?.name?.toLowerCase().includes('video');

    const handleGroupChange = (groupId) => {
        const group = catalogGroups.find((item) => item.id === groupId);
        handleChange('catalog_group_id', groupId);
        if (!group) {
            handleChange('material_type_id', '');
            return;
        }
        if (!group.showSubFilter && group.types.length === 1) {
            handleChange('material_type_id', group.types[0].id);
            return;
        }
        if (group.options.length === 1 && group.options[0].types.length === 1) {
            handleChange('material_type_id', group.options[0].types[0].id);
            return;
        }
        handleChange('material_type_id', '');
    };

    const handleSubcategoryChange = (queryId) => {
        const option = selectedGroup?.options.find((item) => item.queryId === queryId || item.id === queryId);
        const typeId = option?.types[0]?.id || '';
        handleChange('material_type_id', typeId);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'UPDATE UPLOAD' : 'CREATE NEW UPLOAD'}>
            <form onSubmit={handleSubmit} className="space-y-4 ">
                <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Title
                    </label>
                    <textarea
                        id="title"
                        autoComplete="off"
                        type="text"
                        placeholder="Enter title"
                        name="title"
                        value={form?.title || ''}
                        onChange={(e) => handleChange('title', e.target.value)}
                        className={clsx(inputStyles, errors?.title && 'border-red-500 dark:border-red-400')}
                    />
                    {errors?.title && <p className={errorStyles}>{errors?.title}</p>}
                </div>

                <div>
                    <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Date
                    </label>
                    <input
                        id="date"
                        autoComplete="off"
                        type="date"
                        placeholder="Select date"
                        name="date"
                        value={form?.date || ''}
                        onChange={(e) => handleChange('date', e.target.value)}
                        className={clsx(inputStyles, errors?.date && 'border-red-500 dark:border-red-400')}
                    />
                    {errors?.date && <p className={errorStyles}>{errors?.date}</p>}
                </div>

                <div>
                    <label htmlFor="catalog_group_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Category
                    </label>
                    <select
                        id="catalog_group_id"
                        name="catalog_group_id"
                        value={form?.catalog_group_id || selectedGroup?.id || ''}
                        onChange={(e) => handleGroupChange(e.target.value)}
                        className={clsx(inputStyles, errors?.catalog_group_id && 'border-red-500 dark:border-red-400')}
                    >
                        <option value="">Select Category</option>
                        {catalogGroups.map((group) => (
                            <option key={group.id} value={group.id}>
                                {group.label}
                            </option>
                        ))}
                    </select>
                    {errors?.catalog_group_id && <p className={errorStyles}>{errors?.catalog_group_id}</p>}
                </div>

                {showSubcategory ? (
                    <div>
                        <label htmlFor="material_type_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Subcategory
                        </label>
                        <select
                            id="material_type_id"
                            name="material_type_id"
                            value={selectedOption?.queryId || ''}
                            onChange={(e) => handleSubcategoryChange(e.target.value)}
                            className={clsx(inputStyles, errors?.material_type_id && 'border-red-500 dark:border-red-400')}
                        >
                            <option value="">Select Subcategory</option>
                            {selectedGroup?.options.map((option) => (
                                <option key={option.id} value={option.queryId}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        {errors?.material_type_id && <p className={errorStyles}>{errors?.material_type_id}</p>}
                    </div>
                ) : null}

                <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description
                    </label>
                    <textarea
                        id="description"
                        autoComplete="off"
                        placeholder="Enter Description"
                        name="description"
                        value={form?.description || ''}
                        onChange={(e) => handleChange('description', e.target.value)}
                        className={clsx(inputStyles, errors?.description && 'border-red-500 dark:border-red-400')}
                    />
                    {errors?.description && <p className={errorStyles}>{errors?.description}</p>}
                </div>

                {!isAudioOrVideo && (
                    <>
                        <div>
                            <label htmlFor="manual" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Manual
                            </label>
                            <input
                                id="manual"
                                autoComplete="off"
                                type="file"
                                placeholder="Upload Manual"
                                name="manual"
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => {
                                    handleFileChange('manual', e.target.files[0]);
                                    handleChange('manual', e.target.value);
                                }}
                                className={clsx(inputStyles, errors?.manual && 'border-red-500 dark:border-red-400')}
                            />
                            <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, DOC, DOCX (Max size: 10MB)</p>
                            {errors?.manual && <p className={errorStyles}>{errors?.manual}</p>}
                        </div>

                        <div>
                            <label htmlFor="thumbnail" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Thumbnail
                            </label>
                            <input
                                id="thumbnail"
                                autoComplete="off"
                                type="file"
                                placeholder="Upload Thumbnail"
                                name="thumbnail"
                                accept=".jpg,.jpeg,.png"
                                onChange={(e) => {
                                    handleFileChange('thumbnail', e.target.files[0]);
                                    handleChange('thumbnail', e.target.value);
                                }}
                                className={clsx(inputStyles, errors?.thumbnail && 'border-red-500 dark:border-red-400')}
                            />
                            <p className="text-xs text-gray-500 mt-1">Accepted formats: JPG, PNG, JPEG (Max size: 5MB)</p>
                            {errors?.thumbnail && <p className={errorStyles}>{errors?.thumbnail}</p>}
                        </div>
                    </>
                )}

                {isAudioOrVideo && (
                    <div>
                        <label htmlFor="url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            URL
                        </label>
                        <input
                            id="url"
                            autoComplete="off"
                            type="text"
                            placeholder="Enter URL"
                            name="url"
                            value={form?.url || ''}
                            onChange={(e) => handleChange('url', e.target.value)}
                            className={clsx(inputStyles, errors?.url && 'border-red-500 dark:border-red-400')}
                        />
                        <p className="text-xs text-gray-500 mt-1">Enter the URL for your audio/video content</p>
                        {errors?.url && <p className={errorStyles}>{errors?.url}</p>}
                    </div>
                )}

                <ActionButtons isLoading={isLoading} onClose={onClose} onSubmit={handleSubmit} isEdit={isEdit} label={labels} />
            </form>
        </Modal>
    );
};

export default React.memo(UploadModal);
